import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { Modal, Button } from 'common/components';
import { RequestStep, VerifyStep, SuccessStep, RequestFormData, VerifyFormData } from './components';

import styles from './ForgotPasswordModal.module.scss';

type ForgotPasswordModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

type ForgotPasswordStep = 'request' | 'verify' | 'success';

const modalTitles: Record<ForgotPasswordStep, string> = {
    request: 'Reset Your Password',
    verify: 'Verify & Set New Password',
    success: ' ',
};

export const ForgotPasswordModal = ({ isOpen, onClose }: ForgotPasswordModalProps) => {
    const [step, setStep] = useState<ForgotPasswordStep>('request');
    const [cognitoUser, setCognitoUser] = useState<AmazonCognitoIdentity.CognitoUser | null>(null);
    const [userEmail, setUserEmail] = useState('');
    const [userPassword, setUserPassword] = useState('');

    // Form for email request step
    const requestForm = useForm<RequestFormData>({
        mode: 'onSubmit',
        defaultValues: {
            email: '',
        },
    });

    // Form for verification step
    const verifyForm = useForm<VerifyFormData>({
        mode: 'onSubmit',
        defaultValues: {
            verificationCode: '',
            newPassword: '',
            confirmPassword: '',
        },
    });

    const resetForm = () => {
        setStep('request');
        setUserEmail('');
        setUserPassword('');
        setCognitoUser(null);
        requestForm.reset();
        verifyForm.reset();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleRequestSuccess = (email: string, cognitoUser: AmazonCognitoIdentity.CognitoUser) => {
        setUserEmail(email);
        setCognitoUser(cognitoUser);
        setStep('verify');
    };

    const handleVerifySuccess = (email: string, password: string) => {
        setUserPassword(password);
        setStep('success');
    };

    const footerContent: Record<ForgotPasswordStep, React.ReactNode> = {
        success: (
            <Button variant='primary' type='submit' form='login-form'>
                Login & Continue to Dashboard
            </Button>
        ),
        verify: (
            <>
                <Button variant='outline-secondary' onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant='primary' type='submit' form='verify-form'>
                    Reset Password
                </Button>
            </>
        ),
        request: (
            <>
                <Button variant='outline-secondary' onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant='primary' type='submit' form='request-form'>
                    Send Reset Code
                </Button>
            </>
        ),
    };

    const modalContent: Record<ForgotPasswordStep, React.ReactNode> = {
        request: <RequestStep form={requestForm} onSuccess={handleRequestSuccess} />,
        verify: cognitoUser && (
            <VerifyStep
                form={verifyForm}
                userEmail={userEmail}
                cognitoUser={cognitoUser}
                onSuccess={handleVerifySuccess}
                onBackToRequest={() => setStep('request')}
            />
        ),
        success: <SuccessStep userEmail={userEmail} userPassword={userPassword} onLoginSuccess={handleClose} />,
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={modalTitles[step]}
            size='sm'
            showDefaultFooter={false}
            footer={<div className={styles.footer}>{footerContent[step]}</div>}
            closeOnOverlayClick={true}
            closeOnEscape={true}
        >
            <div className={styles.content}>{modalContent[step]}</div>
        </Modal>
    );
};

export default ForgotPasswordModal;
